// src/pages/customer/customerpanel/ProfilePanel.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';

// ── Validation helpers ──────────────────────────────────────────────────────
const VALIDATORS = {
  name:    { re: /^[A-Za-z\s]{2,50}$/,   msg: 'Name must be 2–50 letters only (no numbers or symbols).' },
  phone:   { re: /^[6-9]\d{9}$/,          msg: 'Enter a valid 10-digit Indian mobile number.' },
  pincode: { re: /^\d{6}$/,               msg: 'Pincode must be exactly 6 digits.' },
};

function validate(form) {
  if (!form.name.trim())                        return 'Full name is required.';
  if (!VALIDATORS.name.re.test(form.name.trim())) return VALIDATORS.name.msg;
  if (form.phone && !VALIDATORS.phone.re.test(form.phone))
                                                return VALIDATORS.phone.msg;
  if (form.pincode && !VALIDATORS.pincode.re.test(form.pincode))
                                                return VALIDATORS.pincode.msg;
  return '';
}

// ── Generic display / edit field ────────────────────────────────────────────
const Field = ({ label, icon, value, editing, type = 'text', onChange, placeholder, readOnly }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-black uppercase tracking-widest text-secondary flex items-center gap-1.5">
      {icon && <span className="material-symbols-outlined text-[13px]">{icon}</span>}
      {label}
    </label>
    {editing && !readOnly ? (
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || label}
        className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/40
                   bg-surface-container-low text-sm outline-none
                   focus:ring-2 focus:ring-primary/20 focus:border-primary/40
                   transition-all placeholder:text-outline-variant"
      />
    ) : (
      <div className="flex items-center gap-2 px-1">
        <p className="text-sm font-bold text-on-surface">{value || '—'}</p>
        {readOnly && (
          <span className="text-[10px] bg-surface-container text-secondary px-2 py-0.5 rounded-full">
            Cannot be changed
          </span>
        )}
      </div>
    )}
  </div>
);

// ── Password field with eye-icon toggle ─────────────────────────────────────
const PwdField = ({ label, value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-black uppercase tracking-widest text-secondary">
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-4 py-2.5 pr-10 rounded-xl border border-outline-variant/40
                     bg-surface-container-low text-sm outline-none
                     focus:ring-2 focus:ring-primary/20 focus:border-primary/40
                     transition-all placeholder:text-outline-variant"
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          className="absolute inset-y-0 right-3 flex items-center text-outline
                     hover:text-on-surface transition-colors"
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          <span className="material-symbols-outlined text-[18px]">
            {show ? 'visibility_off' : 'visibility'}
          </span>
        </button>
      </div>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
export default function ProfilePanel({ user, onUserUpdate }) {
  const { updateUser } = useAuth(); 
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    name:         user?.name    || '',
    phone:        user?.phone   || '',
    businessName: user?.businessName || '', 
    address:      user?.address || '',
    city:         user?.city    || '',
    state:        user?.state   || '',
    pincode:      user?.pincode || '',
  });

  useEffect(() => {
    setForm({
      name:         user?.name    || '',
      phone:        user?.phone   || '',
      businessName: user?.businessName || '', 
      address:      user?.address || '',
      city:         user?.city    || '',
      state:        user?.state   || '',
      pincode:      user?.pincode || '',
    });
  }, [user]);

  const [showPwd,    setShowPwd]    = useState(false);
  const [pwdForm,    setPwdForm]    = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdError,   setPwdError]   = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdSaving,  setPwdSaving]  = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const set    = field => val => setForm(prev => ({ ...prev, [field]: val }));
  const setPwd = field => e   => setPwdForm(prev => ({ ...prev, [field]: e.target.value }));

  // ── Profile save ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    const validationError = validate(form);
    if (validationError) { setError(validationError); return; }

    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/api/customer/profile', form);
      setSuccess('Profile updated successfully!');
      setEditing(false);
      if (onUserUpdate) onUserUpdate({ ...user, ...form });
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update profile. Please try again.');
    } finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  setImgUploading(true);
  setError('');
  try {
    const formData = new FormData();
    formData.append('image', file);
    const { data } = await api.put('/api/customer/profile-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    updateUser({ profile_image: data.profile_image });
    if (onUserUpdate) onUserUpdate({ ...user, profile_image: data.profile_image });
  } catch (err) {
    setError('Image upload failed. Please try again.');
  } finally {
    setImgUploading(false);
  }
};

  const handleCancel = () => {
    setForm({
      name:         user?.name    || '',
      phone:        user?.phone   || '',
      businessName: user?.businessName || '', 
      address:      user?.address || '',
      city:         user?.city    || '',
      state:        user?.state   || '',
      pincode:      user?.pincode || '',
    });
    setEditing(false);
    setError('');
  };

  // ── Phone: allow only digits, max 10 ──────────────────────────────────────
  const handlePhoneChange = val => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    set('phone')(digits);
  };

  // ── Pincode: allow only digits, max 6 ─────────────────────────────────────
  const handlePincodeChange = val => {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    set('pincode')(digits);
  };

  // ── Password change ────────────────────────────────────────────────────────
  const handlePasswordChange = async () => {
    if (!pwdForm.current || !pwdForm.newPwd || !pwdForm.confirm) {
      setPwdError('All password fields are required.'); return;
    }
    if (pwdForm.newPwd !== pwdForm.confirm) {
      setPwdError('New passwords do not match.'); return;
    }
    if (pwdForm.newPwd.length < 6) {
      setPwdError('New password must be at least 6 characters.'); return;
    }
    setPwdSaving(true); setPwdError(''); setPwdSuccess('');
    try {
      await api.put('/api/customer/change-password', {
        current_password: pwdForm.current,
        new_password:     pwdForm.newPwd,
      });
      setPwdSuccess('Password changed successfully!');
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      setShowPwd(false);
      setTimeout(() => setPwdSuccess(''), 3500);
    } catch (err) {
      setPwdError(err.response?.data?.error || 'Could not change password. Check your current password.');
    } finally { setPwdSaving(false); }
  };

  // Safe date parse — DB returns "2026-04-17 03:44:55" (no T)
  const memberSince = user?.created_at
    ? new Date(user.created_at.replace(' ', 'T')).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Page header */}
      <div>
        <h2 className="text-xl font-black text-on-surface">My Profile</h2>
        <p className="text-sm text-secondary mt-1">Manage your account details.</p>
      </div>

      {/* ── Profile card ─────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">

        {/* Avatar + edit toggle */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div
              className="relative flex-shrink-0 group cursor-pointer"
              onClick={() => document.getElementById('avatar-input').click()}
            >
              {user?.profile_image ? (
                <img
                  src={user.profile_image}
                  alt="avatar"
                  className="w-16 h-16 rounded-full object-cover border-4 border-primary/20"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary flex items-center
                                justify-center text-white font-black text-2xl border-4 border-primary/20">
                  {(user?.name || 'C')[0].toUpperCase()}
                </div>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100
                              transition-opacity flex items-center justify-center">
                {imgUploading
                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <span className="material-symbols-outlined text-white text-[18px]">photo_camera</span>
                }
              </div>

              {/* Active badge */}
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full
                              bg-emerald-500 border-2 border-white flex items-center justify-center">
                <span className="material-symbols-outlined text-[11px] text-white">check</span>
              </div>

              <input
                id="avatar-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>
            <div>
              <p className="font-black text-on-surface text-base leading-tight">
                {user?.name || 'Customer'}
              </p>
              <p className="text-xs text-secondary mt-0.5">{user?.email}</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span>
                  {{ customer: 'Customer', farmer: 'Farmer', butcher: 'Butcher', admin: 'Admin' }[user?.role] || 'User'} Account
                </span>
                <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Active
                </span>
              </div>
            </div>
          </div>

          {!editing ? (
            <button onClick={() => { setEditing(true); setError(''); setSuccess(''); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl
                         border border-outline-variant/30 text-secondary text-xs font-bold
                         hover:bg-surface-container hover:border-primary/30 hover:text-primary
                         transition-all">
              <span className="material-symbols-outlined text-[16px]">edit</span>
              Edit Profile
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleCancel}
                className="px-3 py-2 rounded-xl border border-outline-variant/30
                           text-secondary text-xs font-bold hover:bg-surface-container
                           transition-all">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary
                           text-white text-xs font-bold hover:opacity-90
                           disabled:opacity-50 transition-all">
                {saving
                  ? <span className="w-3 h-3 border-2 border-white border-t-transparent
                                     rounded-full animate-spin" />
                  : <span className="material-symbols-outlined text-[16px]">check</span>
                }
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        {/* Inline alerts */}
        {(success || error) && (
          <div className={`mx-6 mb-3 px-4 py-2.5 rounded-xl text-xs font-bold
                           flex items-center gap-2 ${
            success
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            <span className="material-symbols-outlined text-[16px]">
              {success ? 'check_circle' : 'error'}
            </span>
            {success || error}
          </div>
        )}

        {/* Form fields */}
        <div className="px-6 pb-6 border-t border-outline-variant/10 pt-5 space-y-4">

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name" icon="person"
              value={form.name} editing={editing}
              onChange={set('name')} placeholder="e.g. Arjun Singh" />

            {/* Phone — custom handler strips non-digits */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-secondary flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[13px]">call</span>
                Phone
              </label>
              {editing ? (
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center
                                   text-[11px] font-bold text-outline pointer-events-none">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => handlePhoneChange(e.target.value)}
                    placeholder="10-digit number"
                    maxLength={10}
                    pattern="[6-9][0-9]{9}"
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-outline-variant/40
                               bg-surface-container-low text-sm outline-none
                               focus:ring-2 focus:ring-primary/20 focus:border-primary/40
                               transition-all placeholder:text-outline-variant"
                  />
                </div>
              ) : (
                <div className="px-1">
                  <p className="text-sm font-bold text-on-surface">
                    {form.phone ? `+91 ${form.phone}` : '—'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <Field label="Email" icon="mail"
            value={user?.email || '—'}
            editing={editing} readOnly />

          <Field label="Delivery Address" icon="home"
            value={form.address} editing={editing}
            onChange={set('address')} placeholder="House no., Street, Area" />

          <div className="grid grid-cols-3 gap-3">
            <Field label="City" icon="location_city"
              value={form.city} editing={editing}
              onChange={set('city')} placeholder="City" />
            <Field label="State" icon="map"
              value={form.state} editing={editing}
              onChange={set('state')} placeholder="State" />

            {/* Pincode — digits only, max 6 */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-secondary flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[13px]">pin_drop</span>
                Pincode
              </label>
              {editing ? (
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.pincode}
                  onChange={e => handlePincodeChange(e.target.value)}
                  placeholder="6-digit"
                  maxLength={6}
                  className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/40
                             bg-surface-container-low text-sm outline-none
                             focus:ring-2 focus:ring-primary/20 focus:border-primary/40
                             transition-all placeholder:text-outline-variant"
                />
              ) : (
                <div className="px-1">
                  <p className="text-sm font-bold text-on-surface">{form.pincode || '—'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Member since */}
          {memberSince && (
            <div className="pt-2 border-t border-outline-variant/10">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-widest text-secondary
                              flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[13px]">calendar_month</span>
                  Member Since
                </p>
                <p className="text-xs font-bold text-secondary">{memberSince}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Change Password ───────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">

        <button
          onClick={() => { setShowPwd(!showPwd); setPwdError(''); setPwdSuccess(''); }}
          className="w-full px-6 py-4 flex items-center justify-between
                     hover:bg-surface-container/40 transition-all text-left">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-surface-container flex items-center
                            justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-[18px] text-secondary">lock</span>
            </div>
            <div>
              <p className="text-sm font-black text-on-surface">Change Password</p>
              <p className="text-[11px] text-secondary">Update your login credentials</p>
            </div>
          </div>
          <span className="material-symbols-outlined text-[20px] text-secondary flex-shrink-0">
            {showPwd ? 'expand_less' : 'expand_more'}
          </span>
        </button>

        {showPwd && (
          <div className="px-6 pb-6 pt-4 border-t border-outline-variant/10 space-y-3">

            {pwdSuccess && (
              <div className="px-4 py-2.5 rounded-xl bg-emerald-50 text-emerald-700
                              border border-emerald-200 text-xs font-bold flex items-center gap-2">
                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                {pwdSuccess}
              </div>
            )}
            {pwdError && (
              <div className="px-4 py-2.5 rounded-xl bg-red-50 text-red-700
                              border border-red-200 text-xs font-bold flex items-center gap-2">
                <span className="material-symbols-outlined text-[16px]">error</span>
                {pwdError}
              </div>
            )}

            {/* 3 password fields each with their own eye toggle via PwdField */}
            <PwdField
              label="Current Password"
              value={pwdForm.current}
              onChange={setPwd('current')}
              placeholder="Enter your current password"
            />
            <PwdField
              label="New Password"
              value={pwdForm.newPwd}
              onChange={setPwd('newPwd')}
              placeholder="At least 6 characters"
            />
            <PwdField
              label="Confirm New Password"
              value={pwdForm.confirm}
              onChange={setPwd('confirm')}
              placeholder="Re-enter new password"
            />

            <button
              onClick={handlePasswordChange}
              disabled={pwdSaving}
              className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-bold
                         hover:opacity-90 disabled:opacity-50 transition-all mt-1
                         flex items-center justify-center gap-2">
              {pwdSaving
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent
                                   rounded-full animate-spin" />
                : <span className="material-symbols-outlined text-[18px]">lock_reset</span>
              }
              {pwdSaving ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        )}
      </div>

      {/* ── Delete Account ───────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden">
        <button
          onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
          className="w-full px-6 py-4 flex items-center justify-between
                     hover:bg-red-50/40 transition-all text-left">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-[18px] text-red-500">delete</span>
            </div>
            <div>
              <p className="text-sm font-black text-red-600">Delete Account</p>
              <p className="text-[11px] text-secondary">Permanently delete your account and data</p>
            </div>
          </div>
          <span className="material-symbols-outlined text-[20px] text-secondary flex-shrink-0">
            {showDeleteConfirm ? 'expand_less' : 'expand_more'}
          </span>
        </button>

        {showDeleteConfirm && (
          <div className="px-6 pb-6 pt-4 border-t border-red-100 space-y-3">
            <p className="text-sm text-red-600 font-medium">
              ⚠️ This action is permanent. Your account, orders, and data will be deleted.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 rounded-xl border border-outline-variant/30
                           text-secondary text-xs font-bold hover:bg-surface-container transition-all">
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await api.delete('/api/customer/account');
                    localStorage.clear();
                    window.location.href = '/';
                  } catch (err) {
                    setError('Could not delete account. Please try again.');
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-xs font-bold
                           hover:bg-red-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                {deleting
                  ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <span className="material-symbols-outlined text-[16px]">delete_forever</span>
                }
                {deleting ? 'Deleting…' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}